// @file Surface post action store
// TODO: [to-be-migrated][post_action]
import { CONFIRM_MODERATION_REJECT_POST } from '@@/bits/confirmation_dialog'
import { captureFetchException, captureMessage } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import { isPhotoAlbum } from '@@/bits/post_attachment'
import { getPollFromPost } from '@@/bits/surface_polls'
import type { PostGroupingInfo } from '@@/bits/surface_posts_grouping_helper'
import {
    getCustomPropIdFromWishGroupBy,
    getOrderedColorGroupsWithTitle,
    getPostGroupingParams,
    isGroupByCustomProp
} from '@@/bits/surface_posts_grouping_helper'
import { WallWishGroupByTypes, WallWishGroupByValue } from '@@/enums'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import {
    OzConfirmationDialogBoxButtonScheme,
    useGlobalConfirmationDialogStore
} from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useScreenRecorderStore } from '@@/pinia/screen_recorder_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import { PhotoAlbumZipApi } from '@@/surface/api/photo_album_zip'
import { WallApi } from '@@/surface/api/wall'
import { WallCustomPostPropertiesApi } from '@@/surface/api/wall_custom_post_properties'
import { WishApi, type WishCopyParams, type WishTransferParams } from '@@/surface/api/wish'
import { fetchJson } from '@@/surface/api_fetch'
import type {
    Cid,
    HashId,
    Id,
    Post,
    PostAttributes,
    PostColor,
    PostColorId,
    PostCustomProperties,
    Section,
    SingleSelectOption,
    WallCustomPostSingleSelectProperty,
    WallId,
    WallWishGroupBy,
    WishArrangement
} from '@@/types'
import { useCopyToClipboard } from '@@/vuecomposables/copy_to_clipboard'
import type { CopyOrTransferPostResult, RootState as SurfaceRootState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { HTTPMethod } from '@padlet/fetch'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

interface CopyXferPostParams {
  wallHashid: HashId
  wallDescription?: string
  wallTitle?: string
  wallSectionId?: Id
  isDestinationThisWall: boolean
  postPosition?: { left: number; top: number }
  postId: Id
  postHashid: HashId
  transferredPostCid: Cid
  libraryId?: string
  transferCustomPropValuesAcrossWalls: boolean
  sortIndex?: number
  pinnedAt?: string | null
  groupingInfo?: PostGroupingInfo
}

interface DestinationWallGrouping {
  groupBy: WallWishGroupBy
  groups: Array<{ id: HashId | PostColorId; title: string }>
}

const useSurfacePostActionStore = defineStore('postAction', () => {
  const vuexStore = getVuexStore<SurfaceRootState>()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  const expandedPostStore = useExpandedPostStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
  const surfaceStore = useSurfaceStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfacePostConnectionStore = useSurfacePostConnectionStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceAttachmentStore = useSurfaceAttachmentsStore()
  const contentPickerStore = useContentPickerStore()
  const screenRecorderStore = useScreenRecorderStore()

  const { copyToClipboard } = useCopyToClipboard()

  // State
  const reportedPostId = ref<Id | null>(null)

  /* ---------------------- */
  /* POLL                   */
  /* ---------------------- */

  const closePoll = async (payload: { post: Post }): Promise<void> => {
    try {
      const pollParams = {
        ...getPollFromPost(payload.post),
        closed_at: new Date().toISOString(),
        wish_id: payload.post.id,
      }
      const { PollApi } = await import('@@/surface/api/poll')
      const updatedWish = await PollApi.updatePoll(pollParams)
      // `as unknown as PostAttributes` is a hacky way to make the types we define in rails and arvo match, it's dangerous but will have to do for now
      surfacePostsStore.updatePostInStore(updatedWish as unknown as PostAttributes)
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('Closed poll'),
      })
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'closePoll' })
    }
  }

  const reopenPoll = async (payload: { post: Post }): Promise<void> => {
    try {
      const pollParams = {
        ...getPollFromPost(payload.post),
        closed_at: null,
        wish_id: payload.post.id,
      }
      const { PollApi } = await import('@@/surface/api/poll')
      const updatedWish = await PollApi.updatePoll(pollParams)
      // `as unknown as PostAttributes` is a hacky way to make the types we define in rails and arvo match, it's dangerous but will have to do for now
      surfacePostsStore.updatePostInStore(updatedWish as unknown as PostAttributes)
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('Reopened poll'),
      })
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'reopenPoll' })
    }
  }

  /* ---------------------- */
  /* POST UNDER ACTION      */
  /* ---------------------- */
  const postUnderActionCid = ref<Cid | null>(null)

  const postUnderAction = computed<Post | undefined>(() =>
    postUnderActionCid.value != null ? surfacePostsStore.postEntitiesByCid[postUnderActionCid.value] : undefined,
  )

  const xPostAction = computed<boolean>(
    () =>
      postUnderActionCid.value != null ||
      expandedPostStore.expandedPostCid != null ||
      xTransferPostPanel.value ||
      xCopyPostPanel.value,
  )

  // Sent to the mobile app to determine where to render post action button.
  // https://github.com/padlet/mozart/pull/23494
  const postActionMenuTriggerRect = shallowRef<DOMRect | null>(null)
  const postRightClickCoordinates = ref<[number, number] | null>(null)

  const hasPostUnderActionWithConnections = computed<boolean>(() => {
    if (postUnderAction.value == null) return false
    const postId = postUnderAction.value.id
    const postConnections = surfacePostConnectionStore.connections
    return (
      postConnections.filter((connection) => connection.from_wish_id === postId || connection.to_wish_id === postId)
        .length > 0
    )
  })

  const isPostUnderActionUserAuthored = computed<boolean>(() => {
    if (postUnderAction.value != null && postUnderAction.value?.author_id === surfaceCurrentUserStore.currentUser?.id) {
      return true
    }

    // When the post action menu is opened in expanded view, postUnderAction is null.
    // It is null because we want to show a slightly different post action menu in expanded view.
    // Setting postUnderAction to a value would trigger the unexpanded post action menu to be shown.
    // This is why we have to check the expanded post instead to determine if the post is user authored.
    if (
      expandedPostStore.expandedPostCid != null &&
      expandedPostStore.expandedPost?.author_id === surfaceCurrentUserStore.currentUser?.id
    ) {
      return true
    }

    return false
  })

  const doesPostUnderActionHaveAttachment = computed<boolean>(
    () =>
      !isEmpty(postUnderAction.value?.attachment) ||
      !isEmpty(postUnderAction.value?.wish_content?.attachment_props?.url),
  )

  const isPostUnderActionPreviewPost = computed<boolean>(
    () => postUnderAction.value != null && postUnderAction.value.id === surfaceStore.coverPostId,
  )

  const showPostActionMenu = (payload: { postCid: Cid }): void => {
    postUnderActionCid.value = payload.postCid
  }

  const hidePostActionMenu = (): void => {
    postUnderActionCid.value = null
    postActionMenuTriggerRect.value = null
    postRightClickCoordinates.value = null
  }

  const setPostActionMenuTriggerRect = (payload: { postActionMenuTriggerRect: DOMRect | null }): void => {
    postActionMenuTriggerRect.value = payload.postActionMenuTriggerRect
    postRightClickCoordinates.value = null
  }

  const setPostRightClickCoordinates = (payload: { postRightClickCoordinates: [number, number] | null }): void => {
    postRightClickCoordinates.value = payload.postRightClickCoordinates
    postActionMenuTriggerRect.value = null
  }

  /* --------------------------------------- */
  /* POST ACTIONS THAT CAN BE PERFORMED      */
  /* --------------------------------------- */
  const canEditPostUnderAction = computed<boolean>(
    () =>
      !surfaceStore.isFrozen &&
      (surfacePermissionsStore.canIEdit ||
        (surfacePermissionsStore.canIWrite && isPostUnderActionUserAuthored.value)) &&
      // Prevent editing a post while screen recording for that same post
      (screenRecorderStore.isVisible && contentPickerStore.destinationEntityType === 'Post'
        ? contentPickerStore.destinationEntityUid !== postUnderAction.value?.cid
        : true),
  )

  const canDeletePostUnderAction = computed<boolean>(() => canEditPostUnderAction.value) // same as can edit

  const canTransferPostUnderAction = computed<boolean>(
    () =>
      surfacePermissionsStore.amIRegistered &&
      (surfacePermissionsStore.canIEdit || (surfacePermissionsStore.canIWrite && isPostUnderActionUserAuthored.value)),
  )

  const canCopyPostUnderAction = computed<boolean>(() => canTransferPostUnderAction.value) // same as can transfer

  const canOpenInMapApp = computed<boolean>(() => {
    if (!surfaceStore.isMap) return false

    // Check regular view (postUnderAction)
    if (
      postUnderAction.value?.location_point?.latitude != null &&
      postUnderAction.value?.location_point?.longitude != null
    ) {
      return true
    }

    // Check expanded view (expandedPost)
    if (
      expandedPostStore.expandedPostCid != null &&
      expandedPostStore.expandedPost?.location_point?.latitude != null &&
      expandedPostStore.expandedPost?.location_point?.longitude != null
    ) {
      return true
    }

    return false
  })

  const canConnectPostUnderAction = computed<boolean>(
    () => (surfaceStore.isCanvas || surfaceStore.isMap) && surfacePermissionsStore.canIWrite && !surfaceStore.isFrozen,
  )

  const canDisconnectPostUnderAction = computed<boolean>(
    () => canConnectPostUnderAction.value && hasPostUnderActionWithConnections.value,
  )

  const canChangeCoverToPostUnderAction = computed<boolean>(
    () =>
      surfacePermissionsStore.canIAdminister &&
      doesPostUnderActionHaveAttachment.value &&
      postUnderAction.value?.published === true &&
      !surfaceStore.isFrozen,
  )

  const canChangePostUnderActionLocation = computed<boolean>(
    () =>
      surfaceStore.isMap &&
      (surfacePermissionsStore.canIEdit || (surfacePermissionsStore.canIWrite && isPostUnderActionUserAuthored.value)),
  )

  const canChangePostStackOrder = computed<boolean>(
    () => (surfaceStore.isCanvas || surfaceStore.isMap) && surfacePermissionsStore.canIEdit && !surfaceStore.isFrozen,
  )

  // TODO: [to-be-migrated][expanded_post]
  const canChangeCoverToExpandedPost = computed<boolean>(
    () =>
      surfacePermissionsStore.canIAdminister &&
      expandedPostStore.doesExpandedPostHaveAttachment &&
      expandedPostStore.expandedPost?.published === true &&
      !surfaceStore.isFrozen,
  )

  /* ------------------------- */
  /* POST ACTIONS - TRANSFER   */
  /* ------------------------- */
  const xTransferPostPanel = ref(false)
  const postBeingTransferredCid = ref<Cid | null>(null)
  const postBeingTransferred = computed<Post | null>(() =>
    postBeingTransferredCid.value != null ? surfacePostsStore.postEntitiesByCid[postBeingTransferredCid.value] : null,
  )
  const copyOrTransferPostResult = ref<CopyOrTransferPostResult | null>(null)
  const copyOrTransferSectionId = ref<Id | null>(null)
  const destinationWallSections = ref<Section[]>([])
  const destinationWallGrouping = ref<DestinationWallGrouping | null>(null)

  const startPostTransfer = (payload: { transferPostCid: Cid }): void => {
    xTransferPostPanel.value = true
    postBeingTransferredCid.value = payload.transferPostCid
  }

  const endPostTransfer = (): void => {
    xTransferPostPanel.value = false
    postBeingTransferredCid.value = null
    copyOrTransferPostResult.value = null
  }

  const actuallyTransferPost = (params: CopyXferPostParams): void => {
    const transferPost = async (): Promise<void> => {
      const { isDestinationThisWall, transferredPostCid, wallTitle, wallDescription, groupingInfo } = params

      let postBeingTransferred: Post | null = null
      if (!isDestinationThisWall) {
        // Optimistically remove the post being transferred to a different wall
        postBeingTransferred = surfacePostsStore.postEntitiesByCid[transferredPostCid]
        void vuexStore?.dispatch('post/removePost', { cid: transferredPostCid }, { root: true })
      }
      try {
        const transferParams: WishTransferParams = {
          wishHashid: params.postHashid,
          wallHashid: params.wallHashid,
          wallTitle,
          wallDescription,
          libraryId: params.libraryId,
          transferCustomPropValuesAcrossWalls: params.transferCustomPropValuesAcrossWalls,
        }

        // Handle selected grouping
        const groupingParams = getPostGroupingParams(groupingInfo)
        Object.assign(transferParams, groupingParams)

        const response = await WishApi.transfer(transferParams)
        const postAttributes = Array.isArray(response.data) ? response.data[0]?.attributes : response.data?.attributes
        const isDestinationNewWall = !isEmpty(wallTitle) || !isEmpty(wallDescription)

        await updatePostTransferred({
          newPostData: postAttributes,
          isDestinationThisWall,
          isDestinationNewWall,
        })
      } catch (e) {
        // contents of error
        // {
        //    "response": { /* response object */ },
        //    "status": 422, /* some 4xx error */
        //    "message": "{ \"error\": \"some error message\" }"
        // }
        // check to see if "message" can be JSON decoded
        let parsedError = e
        try {
          parsedError = JSON.parse(e.message)
        } finally {
          if (!isDestinationThisWall) {
            // Reinstate the removed post since the transfer failed
            void vuexStore?.dispatch('post/revivePost', postBeingTransferred, { root: true })
          }
          await updatePostTransferred({ error: parsedError })
        }
      }
    }
    const customRequest = { key: 'TRANSFER_POST', method: transferPost }
    copyOrTransferSectionId.value = params.wallSectionId ?? null
    void vuexStore?.dispatch(
      'post/enqueueRequest',
      {
        post: { id: params.postId, hashid: params.postHashid, cid: params.transferredPostCid },
        customRequest,
      },
      { root: true },
    )
  }

  const updatePostTransferred = async ({
    error,
    newPostData,
    isDestinationThisWall = false,
    isDestinationNewWall = false,
  }: {
    error?: { error?: string }
    newPostData?: PostAttributes
    isDestinationNewWall?: boolean
    isDestinationThisWall?: boolean
  }): Promise<void> => {
    const result: CopyOrTransferPostResult = {
      status: 'success',
      operation: 'transfer',
      isDestinationThisWall,
      isDestinationNewWall,
    }
    if (error != null) {
      result.status = 'error'
      result.text = __('Sorry, we failed to transfer your post. %{cause_of_failure}', {
        cause_of_failure: error.error ?? '',
      })
    } else {
      let wallInfo = surfaceStore.wallAttributes
      if (!isDestinationThisWall) {
        try {
          if (newPostData?.wall_hashid != null) {
            wallInfo = await WallApi.fetch(newPostData?.wall_hashid)
          } else {
            // TODO: Systemic Authorization issues - remove fetch by id
            wallInfo = await WallApi.read({ id: newPostData?.wall_id })
          }
        } catch (e) {
          captureFetchException(e, { source: 'updatePostTransferred#Wall.read' })
        }
      } else {
        void vuexStore?.dispatch('post/updatePostInStore', newPostData, { root: true })
      }
      result.url = wallInfo.links?.show
      result.text = __('Post transferred to %{padletTitle}', { padletTitle: wallInfo.title })
    }
    if (result.status !== 'error') {
      postBeingTransferredCid.value = null
    }
    copyOrTransferPostResult.value = result
  }

  /* ------------------------- */
  /* POST ACTIONS - COPYING    */
  /* ------------------------- */
  const xCopyPostPanel = ref(false)
  const postBeingCopiedCid = ref<Cid | null>(null)
  const postBeingCopied = computed<Post | null>(() =>
    postBeingCopiedCid.value != null ? surfacePostsStore.postEntitiesByCid[postBeingCopiedCid.value] : null,
  )

  const startPostCopy = (payload: { copyPostCid: Cid }): void => {
    xCopyPostPanel.value = true
    postBeingCopiedCid.value = payload.copyPostCid
  }

  const endPostCopy = (): void => {
    xCopyPostPanel.value = false
    postBeingCopiedCid.value = null
    copyOrTransferPostResult.value = null
  }

  const actuallyCopyPost = (params: CopyXferPostParams): void => {
    if (params.wallSectionId != null) {
      copyOrTransferSectionId.value = params.wallSectionId
    }
    const copyPost = async (): Promise<void> => {
      const { wallTitle, wallDescription, groupingInfo } = params
      try {
        const copyParams: WishCopyParams = {
          wishHashid: params.postHashid,
          wallHashid: params.wallHashid,
          wallTitle,
          wallDescription,
          libraryId: params.libraryId,
          postPosition: params.postPosition,
          transferCustomPropValuesAcrossWalls: !params.isDestinationThisWall,
          sortIndex: params.sortIndex,
          pinnedAt: params.pinnedAt,
        }

        // Handle selected grouping
        const groupingParams = getPostGroupingParams(groupingInfo)
        Object.assign(copyParams, groupingParams)

        const response = await WishApi.copy(copyParams)
        const postAttributes = Array.isArray(response.data) ? response.data[0]?.attributes : response.data?.attributes
        const isDestinationNewWall = params.isDestinationThisWall
          ? false
          : !isEmpty(wallTitle) || !isEmpty(wallDescription)

        await updatePostCopied({
          newPostData: postAttributes,
          isDestinationNewWall,
        })
      } catch (e) {
        // contents of error
        // {
        //    "response": { /* response object */ },
        //    "status": 422, /* some 4xx error */
        //    "message": "{ \"error\": \"some error message\" }"
        // }
        // check to see if "message" can be JSON decoded
        let parsedError = e
        try {
          parsedError = JSON.parse(e.message)
        } finally {
          await updatePostCopied({ error: parsedError })
        }
      }
    }
    const customRequest = { key: 'COPY_POST', method: copyPost }
    void vuexStore?.dispatch(
      'post/enqueueRequest',
      {
        post: { id: params.postId, hashid: params.postHashid, cid: params.transferredPostCid },
        customRequest,
      },
      { root: true },
    )
  }

  const updatePostCopied = async ({
    error,
    newPostData,
    isDestinationNewWall = false,
  }: {
    error?: { error?: string }
    newPostData?: PostAttributes
    isDestinationNewWall?: boolean
  }): Promise<void> => {
    const result: CopyOrTransferPostResult = {
      status: 'success',
      operation: 'copy',
      isDestinationThisWall: false,
      isDestinationNewWall,
    }
    if (error != null) {
      result.status = 'error'
      result.text = __('Sorry, we failed to duplicate your post. %{cause_of_failure}', {
        cause_of_failure: error.error ?? '',
      })
    } else {
      result.isDestinationThisWall = !isDestinationNewWall && surfaceStore.wallId === newPostData?.wall_id
      let wallInfo = surfaceStore.wallAttributes
      if (result.isDestinationThisWall) {
        void vuexStore?.dispatch('post/addPost', newPostData, { root: true })
      } else {
        try {
          if (newPostData?.wall_hashid != null) {
            wallInfo = await WallApi.fetch(newPostData?.wall_hashid)
          } else {
            // TODO: Systemic Authorization issues - remove fetch by id
            wallInfo = await WallApi.read({ id: newPostData?.wall_id })
          }
        } catch (e) {
          captureFetchException(e, { source: 'updatePostCopied#Wall.read' })
        }
      }
      result.url = wallInfo.links?.show
      result.text = isDestinationNewWall
        ? __('Post copied to %{padletTitle}', { padletTitle: wallInfo.title })
        : __('Post duplicated successfully')
    }
    copyOrTransferPostResult.value = result
  }

  const fetchDestinationWallSections = async (payload: { wallId: WallId }): Promise<void> => {
    try {
      destinationWallSections.value = []
      const { WallSectionApi } = await import('@@/surface/api/wall_section')
      const response: JsonAPIResponse<Section> = await WallSectionApi.readAll({ wall: { id: payload.wallId } })
      const sections = (response.data as Array<JsonAPIResource<Section>>).map((section) => section.attributes)
      destinationWallSections.value = sections
    } catch (e) {
      captureFetchException(e, { source: 'fetchDestinationWallSections#WallSection.readAll' })
    }
  }

  const fetchDestinationWallGrouping = async (payload: { wallId: WallId; wallAddress: string }): Promise<void> => {
    try {
      destinationWallGrouping.value = null

      // Fetch padlet starting state which includes wish_arrangement
      // TODO: This padlet-starting-state endpoint would load a ton of extra things,
      // we should make a specialized API to fetch just the wish arrangement in future.
      const startingStateResponse = await fetchJson(`/api/8/padlet-starting-state/${payload.wallAddress}`, {
        method: HTTPMethod.get,
      })

      // Extract wish arrangement from the starting state
      const wishArrangement: WishArrangement | undefined = startingStateResponse?.wall?.wish_arrangement as
        | WishArrangement
        | undefined

      const groupBy = wishArrangement?.group_by as WallWishGroupBy | undefined

      // Determine grouping type and configure picker options
      if (groupBy == null) {
        // Destination wall grouped by None: no picker needed
        destinationWallGrouping.value = {
          groupBy: WallWishGroupByTypes.None,
          groups: [],
        }
      } else if (groupBy === WallWishGroupByTypes.Section) {
        // Destination wall grouped by Section: show section picker with available sections
        await fetchDestinationWallSections(payload)
        destinationWallGrouping.value = {
          groupBy: WallWishGroupByTypes.Section,
          groups: destinationWallSections.value.map((section) => ({
            id: section.hashid,
            title: section.title,
          })),
        }
      } else if (groupBy === WallWishGroupByTypes.Color) {
        // Destination wall grouped by Color: show color picker with available colors
        destinationWallGrouping.value = {
          groupBy: WallWishGroupByTypes.Color,
          groups: getOrderedColorGroupsWithTitle({
            isLightColorScheme: startingStateResponse?.wall?.color_scheme === 'light',
          }),
        }
      } else if (groupBy === WallWishGroupByTypes.Author) {
        // Destination wall grouped by Author: no picker needed (auto-groups by author)
        destinationWallGrouping.value = {
          groupBy: WallWishGroupByTypes.Author,
          groups: [],
        }
      } else if (isGroupByCustomProp(groupBy)) {
        // Destination wall grouped by Single-select custom property: show custom property options
        const propertyId = getCustomPropIdFromWishGroupBy(groupBy)
        if (propertyId) {
          const customProperties = await WallCustomPostPropertiesApi.fetchWallCustomPostProperties(payload.wallId)
          const singleSelectProperty = customProperties.find(
            (prop) => prop.id === propertyId && prop.data_type === 'single_select',
          ) as WallCustomPostSingleSelectProperty | undefined

          if (singleSelectProperty) {
            const singleSelectOptions = singleSelectProperty.selection_options.map((option: SingleSelectOption) => ({
              id: option.id ?? '',
              title: option.name,
            }))

            const singleSelectGroups = singleSelectProperty.required
              ? singleSelectOptions
              : [...singleSelectOptions, { id: WallWishGroupByValue.NoValue, title: __('No value') }]

            destinationWallGrouping.value = {
              groupBy,
              groups: singleSelectGroups,
            }
          }
        }
      }
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'fetchDestinationWallGrouping' })
    }
  }

  /* ---------------------------- */
  /* POST ACTIONS - DELETE        */
  /* ---------------------------- */
  const confirmDeletePost = (payload: { postCid: Cid }): void => {
    globalConfirmationDialogStore.openConfirmationDialog({
      title: __('Delete post?'),
      body: __('Are you sure you want to delete this post? This cannot be undone!'),
      confirmButtonText: __('Delete'),
      cancelButtonText: __('Nevermind'),
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      afterConfirmActions: [
        () => expandedPostStore.unexpandPost(),
        async () => await surfacePostsStore.deletePost(payload.postCid),
      ],
    })
  }

  const deletePostRemote = (post?: Post): void => {
    if (post == null) return

    const postFromStore = vuexStore?.getters['post/getLiveOrDeletedPost'](post)
    if (postFromStore == null) return

    const postCid = postFromStore.cid

    if (postCid === postBeingTransferredCid.value) {
      endPostTransfer()
    }

    if (postCid === postBeingCopiedCid.value) {
      endPostCopy()
    }

    if (postCid === postUnderActionCid.value) {
      hidePostActionMenu()
    }

    if (postCid === expandedPostStore.expandedPostCid) {
      // When the expanded post is deleted, show snackbar and navigate to the next post.
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('Previous post has been deleted elsewhere.'),
      })
      const postIndex = expandedPostStore.expandedPostPostsToNavigate.findIndex(
        (p: Post): boolean => p.cid === expandedPostStore.expandedPostCid,
      )
      if (expandedPostStore.expandedPostPostsToNavigate.length === 1) {
        expandedPostStore.unexpandPost()
      } else if (postIndex === expandedPostStore.expandedPostPostsToNavigate.length - 1) {
        // go to previous post
        expandedPostStore.expandPost({ postCid: expandedPostStore.expandedPostPostsToNavigate[postIndex - 1].cid })
      } else {
        // go to next post
        expandedPostStore.expandPost({ postCid: expandedPostStore.expandedPostPostsToNavigate[postIndex + 1].cid })
      }
    }
  }

  /* ---------------------- */
  /* POST ACTIONS - GENERAL */
  /* ---------------------- */

  const approvePost = (payload: { postCid: Cid }): void => {
    void vuexStore?.dispatch('post/approvePost', payload, { root: true })
  }

  const confirmModerationRejectPost = (payload: { postCid: Cid }): void => {
    globalConfirmationDialogStore.openConfirmationDialog({
      ...CONFIRM_MODERATION_REJECT_POST,
      afterConfirmActions: [
        () => expandedPostStore.unexpandPost(),
        async () =>
          await vuexStore?.dispatch(
            'post/deletePost',
            { ...payload, options: { softDelete: false, trackHistory: false } },
            { root: true },
          ),
      ],
    })
  }

  const copyPostLinkAndAlert = (payload: { postCid: Cid }): void => {
    const post = surfacePostsStore.postEntitiesByCid[payload.postCid]
    if (post?.permalink == null) return
    void copyToClipboard({
      text: post.permalink,
      globalSnackbarOptions: {
        notificationType: SnackbarNotificationType.success,
        message: __('Link copied to clipboard'),
      },
    })
  }

  const copyAttachmentLinkAndAlert = async (payload: { postCid: Cid }): Promise<void> => {
    const post = surfacePostsStore.postEntitiesByCid[payload.postCid]

    if (isPhotoAlbum(post) && post?.hashid) {
      const publicKey = surfaceStore.publicKey
      if (!publicKey) return

      const photoAlbumZipUrl = PhotoAlbumZipApi.buildUrl(PhotoAlbumZipApi.photoAlbumZipUrl, {
        publicKey,
        wishHashid: post.hashid,
        format: 'zip',
      })

      const fullUrl = `${window.location.origin}${photoAlbumZipUrl}`

      void copyToClipboard({
        text: fullUrl,
        globalSnackbarOptions: {
          notificationType: SnackbarNotificationType.success,
          message: __('Link copied to clipboard'),
        },
      })
      return
    }

    // For single attachments, use the existing logic
    const linkAttributes = surfaceAttachmentStore.getLinkDisplayAttributesForPost(payload.postCid)
    let linkImagePreviewUrl: string | undefined
    // for non-upload links pointing to image files, we want to use the url for the image copy stored in padlet-artifacts bucket
    if (linkAttributes != null && linkAttributes.content_category === 'photo') {
      linkImagePreviewUrl = linkAttributes.original_image_url
    }
    const url =
      post?.wish_content?.attachment_props?.hotlink ??
      post?.wish_content?.attachment_props?.url ??
      linkImagePreviewUrl ??
      post?.attachment
    if (url == null) return
    void copyToClipboard({
      text: url,
      globalSnackbarOptions: {
        notificationType: SnackbarNotificationType.success,
        message: __('Link copied to clipboard'),
      },
    })
  }

  const copyMapLocationAndAlert = (payload: { postCid: Cid }): void => {
    const post = surfacePostsStore.postEntitiesByCid[payload.postCid]
    if (post?.location_point == null || post?.location_name == null) return
    // if the point string is the same as the location name, we don't need to include the location name in the string
    const pointString = `(${post.location_point.latitude}, ${post.location_point.longitude})` // "(40.7128, -74.0060)"
    // if the location name is picked from the coordinates, it is "(40.7128, -74.0060)"
    let locationString = `${post.location_name} ${pointString}` // New York, NY (40.7128, -74.0060)
    if (pointString === post?.location_name) {
      locationString = pointString
    }
    void copyToClipboard({
      text: locationString, // "New York, NY (40.7128, -74.0060)" or "(40.7128, -74.0060)"
      globalSnackbarOptions: {
        notificationType: SnackbarNotificationType.success,
        message: __('Location copied to clipboard'),
      },
    })
  }

  // TODO we should not need this method, ideally. Getters and watches should take care of this.
  const recalibrate = (): void => {
    const postEntitiesByCid = useSurfacePostsStore().postEntitiesByCid

    if (postBeingTransferredCid.value != null && postEntitiesByCid[postBeingTransferredCid.value] == null) {
      endPostTransfer()
    }

    if (postBeingCopiedCid.value != null && postEntitiesByCid[postBeingCopiedCid.value] == null) {
      endPostCopy()
    }

    if (postUnderActionCid.value != null && postEntitiesByCid[postUnderActionCid.value] == null) {
      hidePostActionMenu()
    }

    const expandedPostCid = expandedPostStore.expandedPostCid
    if (expandedPostCid != null && postEntitiesByCid[expandedPostCid] == null) {
      expandedPostStore.unexpandPost()
    }
  }

  const changePostColor = (payload: { postCid: Cid; newBgColor: PostColor; groupByChanged?: boolean }): void => {
    void vuexStore?.dispatch('post/changePostColor', payload)
  }

  const removePostColor = (payload: { postCid: Cid }): void => {
    void vuexStore?.dispatch('post/removePostColor', payload)
  }

  const changePostCustomProperties = (payload: { postCid: Cid; customProperties: PostCustomProperties }): void => {
    const post: Post | undefined = surfacePostsStore.postEntitiesByCid[payload.postCid]
    if (post == null) {
      captureMessage('Post not found for postCid when changing post custom properties', {
        context: { postCid: payload.postCid },
      })
      return
    }

    // Merge custom properties
    const mergedProperties = {
      ...post.custom_properties,
      ...payload.customProperties,
    }

    // Filter out null values
    const filteredProperties: PostCustomProperties = Object.fromEntries(
      Object.entries(mergedProperties).filter(([_, value]) => value != null),
    )

    // Update the post with new custom properties
    const updatedPost = {
      ...post,
      custom_properties: filteredProperties,
    }

    surfacePostsStore.updatePostInStore(updatedPost)
  }

  const setCoverPost = async (payload: { postId: Id }): Promise<void> => {
    const wall = await WallApi.update({ ...surfaceStore.wallAttributes, cover_wish_id: payload.postId })
    surfaceStore.updateWall(wall)
    useGlobalSnackbarStore().setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: __('Cover image updated'),
    })
  }

  const unsetCoverPost = async (): Promise<void> => {
    const wall = await WallApi.update({ ...surfaceStore.wallAttributes, cover_wish_id: null })
    surfaceStore.updateWall(wall)
    useGlobalSnackbarStore().setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: __('Cover image updated'),
    })
  }

  const showReportPanel = ({ postId }: { postId?: Id } = {}): void => {
    reportedPostId.value = postId ?? null
    useSurfacePanelsStore().xReportPanel = true
  }

  const hideReportPanel = (): void => {
    reportedPostId.value = null
    useSurfacePanelsStore().xReportPanel = false
  }

  return {
    // State
    reportedPostId,

    // getters
    postUnderActionCid,
    postRightClickCoordinates,
    postActionMenuTriggerRect,
    xTransferPostPanel,
    postBeingTransferredCid,
    copyOrTransferPostResult,
    xCopyPostPanel,
    postBeingCopiedCid,
    destinationWallSections,
    destinationWallGrouping,
    copyOrTransferSectionId,
    hasPostUnderActionWithConnections,
    xPostAction,
    isPostUnderActionUserAuthored,
    canEditPostUnderAction,
    canDeletePostUnderAction,
    canTransferPostUnderAction,
    canCopyPostUnderAction,
    canOpenInMapApp,
    canConnectPostUnderAction,
    canDisconnectPostUnderAction,
    canChangePostStackOrder,
    doesPostUnderActionHaveAttachment,
    canChangeCoverToPostUnderAction,
    canChangeCoverToExpandedPost,
    canChangePostUnderActionLocation,
    isPostUnderActionPreviewPost,
    postUnderAction,
    postBeingCopied,
    postBeingTransferred,

    // actions
    showPostActionMenu,
    setPostActionMenuTriggerRect,
    setPostRightClickCoordinates,
    hidePostActionMenu,
    startPostTransfer,
    endPostTransfer,
    actuallyTransferPost,
    updatePostTransferred,
    startPostCopy,
    endPostCopy,
    actuallyCopyPost,
    updatePostCopied,
    fetchDestinationWallSections,
    fetchDestinationWallGrouping,
    confirmDeletePost,
    approvePost,
    confirmModerationRejectPost,
    copyPostLinkAndAlert,
    copyAttachmentLinkAndAlert,
    copyMapLocationAndAlert,
    closePoll,
    reopenPoll,
    deletePostRemote,
    recalibrate,
    changePostColor,
    removePostColor,
    changePostCustomProperties,
    setCoverPost,
    unsetCoverPost,
    showReportPanel,
    hideReportPanel,
  }
})
export { useSurfacePostActionStore }
export type { DestinationWallGrouping }
